import React, { useState } from "react";
import { ArrowLeft, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function ClientAccount() {
  const { user } = useAuth();

  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr, setPwErr] = useState("");
  const [pwOk, setPwOk] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const changePw = async (e) => {
    e.preventDefault();
    setPwErr(""); setPwOk(false);
    if (pwForm.new_password !== pwForm.confirm) {
      setPwErr("New passwords do not match.");
      return;
    }
    if (pwForm.new_password.length < 8) {
      setPwErr("New password must be at least 8 characters.");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      setPwOk(true);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setPwErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPwBusy(false);
    }
  };

  const inputCls =
    "w-full bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] px-3.5 py-2.5 text-sm text-[color:var(--atech-text)] focus:outline-none focus:border-[color:var(--atech-copper)]";

  return (
    <div className="max-w-2xl mx-auto px-6 sm:px-10 py-12 fade-up" data-testid="client-account-page">
      <Link
        to="/portal"
        className="inline-flex items-center gap-2 text-xs text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] mb-8"
      >
        <ArrowLeft size={14} /> Back to projects
      </Link>

      <div className="label text-[color:var(--atech-copper)] mb-2">Your account</div>
      <h1 className="font-display text-4xl tracking-tight text-[color:var(--atech-text)] mb-10">
        Account
      </h1>

      {/* Profile info */}
      <section className="card-surface p-8 mb-6">
        <div className="label mb-5">Profile</div>
        <dl className="space-y-4">
          <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-4">
            <dt className="label text-[color:var(--atech-text-muted)]">Name</dt>
            <dd className="text-sm text-[color:var(--atech-text)]">{user?.name || "—"}</dd>
          </div>
          <div className="flex items-center justify-between gap-4">
            <dt className="label text-[color:var(--atech-text-muted)]">Email</dt>
            <dd className="text-sm text-[color:var(--atech-text)]">{user?.email || "—"}</dd>
          </div>
        </dl>
        <p className="text-xs text-[color:var(--atech-text-muted)] mt-5">
          To update your name or email, please contact A-Tech directly.
        </p>
      </section>

      {/* Change password */}
      <section className="card-surface p-8">
        <div className="label mb-5">Change password</div>
        {pwOk && (
          <div className="flex items-center gap-2 text-[#34D399] text-sm mb-4" data-testid="pw-change-success">
            <CheckCircle2 size={14} /> Password updated successfully.
          </div>
        )}
        {pwErr && (
          <div className="text-xs text-[#F87171] border border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.08)] rounded-[2px] px-3 py-2 mb-4" data-testid="pw-change-error">
            {pwErr}
          </div>
        )}
        <form onSubmit={changePw} className="space-y-5" data-testid="change-password-form">
          <label className="block">
            <span className="label block mb-2">Current password</span>
            <div className="relative">
              <input
                required
                type={showCurrent ? "text" : "password"}
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                className={inputCls}
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-text)]"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </label>
          <label className="block">
            <span className="label block mb-2">New password</span>
            <div className="relative">
              <input
                required
                type={showNew ? "text" : "password"}
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                minLength={8}
                className={inputCls}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-text)]"
                tabIndex={-1}
              >
                {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </label>
          <label className="block">
            <span className="label block mb-2">Confirm new password</span>
            <input
              required
              type="password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
              minLength={8}
              className={inputCls}
              data-testid="confirm-password-input"
            />
          </label>
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={pwBusy}
              className="btn-copper text-sm"
              data-testid="change-password-submit"
            >
              {pwBusy ? "Saving…" : "Update password"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
